// src/modules/competitions/components/JudgeVideoEvaluationModal.tsx

import React, { useState, useEffect } from 'react';
import Modal from '../../../shared/components/ui/Modal';
import Button from '../../../shared/components/ui/Button';
import Loading from '../../../shared/components/Loading';
import ErrorMessage from '../../../shared/components/ErrorMessage';
import { judgeService } from '../services/judgeService';
import { 
  VideoEvaluationCriterion, 
  CriterionEvaluation 
} from '../types/judge';

interface JudgeVideoEvaluationModalProps {
  isOpen: boolean;
  onClose: () => void;
  videoSubmissionId: number;
  videoUrl: string;
  videoTypeName: string;
  participantCode: string;
  durationSeconds: number;
  videoTypeId: number;
  categoryId: number; // ✅ NUEVO
  level: string; // ✅ NUEVO
  onEvaluationComplete: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  currentIndex?: number;
  totalItems?: number;
}

const JudgeVideoEvaluationModal: React.FC<JudgeVideoEvaluationModalProps> = ({
  isOpen,
  onClose,
  videoSubmissionId,
  videoUrl,
  videoTypeName,
  participantCode,
  durationSeconds,
  videoTypeId,
  categoryId, // ✅ NUEVO
  level, // ✅ NUEVO
  onEvaluationComplete,
  onNavigate,
  currentIndex,
  totalItems
}) => {
  const [criteria, setCriteria] = useState<VideoEvaluationCriterion[]>([]);
  const [videoData, setVideoData] = useState<any>(null);
  const [evaluations, setEvaluations] = useState<Record<number, number>>({});
  const [comments, setComments] = useState<Record<number, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadVideoDetails();
      setVideoLoaded(false);
      setVideoError(false);
    }
  }, [isOpen, videoSubmissionId, videoTypeId, categoryId, level]);

  const loadVideoDetails = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      // ✅ NUEVO: Cargar submissions con criterios Y evaluaciones existentes
      console.log('🔍 Cargando datos completos del video...');
      console.log('  - categoryId:', categoryId);
      console.log('  - level:', level);
      console.log('  - videoTypeId:', videoTypeId);
      console.log('  - videoSubmissionId:', videoSubmissionId);
      
      const response = await judgeService.getVideoSubmissions(categoryId, level, videoTypeId);
      
      console.log('📋 Respuesta completa:', response);
      
      if (!response.success || !response.data) {
        throw new Error('No se pudieron cargar los datos del video');
      }
      
      // Mapear criterios al formato esperado
      const mappedCriteria: VideoEvaluationCriterion[] = response.data.criteria.map((c: any) => ({
        criterion_id: c.criterion_id,
        name: c.name,
        description: c.description,
        min_score: c.min_score,
        max_score: c.max_score,
        weight: c.weight,
        order_index: c.order_index
      }));
      
      console.log('✅ Criterios mapeados:', mappedCriteria);
      
      setCriteria(mappedCriteria);
      
      // Buscar la submission específica
      const foundSubmission = response.data.submissions.find(
        (s: any) => s.submission_id === videoSubmissionId
      );
      
      console.log('🎥 Submission encontrada:', foundSubmission);
      
      if (foundSubmission) {
        setVideoData({
          video_submission_id: videoSubmissionId,
          video_url: videoUrl,
          participant_code: participantCode,
          duration_seconds: durationSeconds
        });
        
        // ✅ Si ya existe evaluación, cargarla
        if (foundSubmission.my_evaluation) {
          console.log('📝 Cargando evaluación existente:', foundSubmission.my_evaluation);
          loadExistingEvaluation(foundSubmission.my_evaluation);
        } else {
          console.log('ℹ️ No hay evaluación previa');
        }
      } else {
        console.warn('⚠️ No se encontró la submission en la respuesta');
        setVideoData({
          video_submission_id: videoSubmissionId,
          video_url: videoUrl,
          participant_code: participantCode,
          duration_seconds: durationSeconds
        });
      }
      
    } catch (err: any) {
      console.error('❌ Error cargando video:', err);
      setError(err.message || 'Error al cargar video para evaluar');
    } finally {
      setIsLoading(false);
    }
  };

  const loadExistingEvaluation = (evaluation: any) => {
    console.log('📋 loadExistingEvaluation recibió:', evaluation);
    console.log('📋 criteria_evaluations:', evaluation.criteria_evaluations);
    
    const evalMap: Record<number, number> = {};
    const commentMap: Record<number, string> = {};
    
    evaluation.criteria_evaluations?.forEach((ce: CriterionEvaluation) => {
      console.log('📋 Procesando criterio:', ce);
      evalMap[ce.criterion_id] = ce.score;
      if (ce.comment) {
        commentMap[ce.criterion_id] = ce.comment;
      }
    });
    
    console.log('📋 evalMap final:', evalMap);
    console.log('📋 commentMap final:', commentMap);
    
    setEvaluations(evalMap);
    setComments(commentMap);
  };

  const handleScoreChange = (criterionId: number, score: number) => {
    setEvaluations(prev => ({
      ...prev,
      [criterionId]: score
    }));
  };

  const handleCommentChange = (criterionId: number, comment: string) => {
    setComments(prev => ({
      ...prev,
      [criterionId]: comment
    }));
  };

  const validateEvaluation = (): boolean => {
    for (const criterion of criteria) {
      const score = evaluations[criterion.criterion_id];
      
      if (score === undefined || score === null) {
        setError(`Falta evaluar: ${criterion.name}`);
        return false;
      }
      
      if (score < criterion.min_score || score > criterion.max_score) {
        setError(`Puntaje inválido en: ${criterion.name}`);
        return false;
      }
    }
    
    return true;
  };

  const calculateTotalScore = (): number => {
    let total = 0;
    criteria.forEach(criterion => {
      const score = evaluations[criterion.criterion_id] || 0;
      total += score;
    });
    return total;
  };

  const handleSave = async (isFinal: boolean) => {
    if (!videoData) return;
    
    if (!validateEvaluation()) return;
    
    try {
      setIsSaving(true);
      setError(null);
      
      const criteriaEvaluations: CriterionEvaluation[] = criteria.map(criterion => ({
        criterion_id: criterion.criterion_id,
        score: evaluations[criterion.criterion_id],
        comment: comments[criterion.criterion_id] || null
      }));

      console.log('💾 Guardando evaluación:', {
        video_submission_id: videoSubmissionId,
        criteria_evaluations: criteriaEvaluations,
        is_final: isFinal
      });

      const response = await judgeService.saveVideoEvaluation({
        video_submission_id: videoSubmissionId,
        criteria_evaluations: criteriaEvaluations,
        is_final: isFinal
      });

      console.log('✅ Respuesta guardado:', response);

      if (response.success) {
        onEvaluationComplete();
        
        if (isFinal) {
          if (onNavigate && currentIndex && totalItems && currentIndex < totalItems) {
            setEvaluations({});
            setComments({});
          } else {
            onClose();
          }
        }
      } else {
        setError(response.message || 'Error al guardar evaluación');
      }
    } catch (err: any) {
      console.error('❌ Error guardando:', err);
      setError(err.message || 'Error al guardar evaluación de video');
    } finally {
      setIsSaving(false);
    }
  };

  const handleVideoLoad = () => {
    setVideoLoaded(true);
    setVideoError(false);
    console.log('✅ Video cargado correctamente');
  };

  const handleVideoError = (e: any) => {
    setVideoError(true);
    setVideoLoaded(true);
    console.error('❌ Error cargando video:', e);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Evaluación de Video - ${videoTypeName}`}
      size="fullscreen"
    >
      {isLoading ? (
        <Loading message="Cargando video..." />
      ) : !videoData ? (
        <div className="no-submissions">
          <p>No hay video disponible</p>
          <Button onClick={onClose}>Cerrar</Button>
        </div>
      ) : (
        <div className="evaluation-container">
          <div className="evaluation-image-section">
            <div className="evaluation-image-wrapper">
              {!videoLoaded && !videoError && (
                <div className="image-loading-overlay">
                  <div className="spinner"></div>
                  <p>Cargando video...</p>
                </div>
              )}

              {videoError && (
                <div className="image-error-overlay">
                  <span className="error-icon">⚠️</span>
                  <p>Error al cargar video</p>
                </div>
              )}

              <video
                controls
                className="evaluation-video"
                src={videoData.video_url}
                onLoadedMetadata={handleVideoLoad}
                onError={handleVideoError}
                style={{
                  opacity: videoLoaded && !videoError ? 1 : 0,
                  transition: 'opacity 0.4s ease-in-out'
                }}
              >
                Tu navegador no soporta el elemento de video.
              </video>
            </div>
            
            <div className="evaluation-meta">
              <p>
                {currentIndex && totalItems ? (
                  <>Video {currentIndex} de {totalItems} | </>
                ) : null}
                Participante: <strong>{videoData.participant_code}</strong> | 
                Duración: <strong>{videoData.duration_seconds}s</strong>
              </p>
            </div>
          </div>

          <div className="evaluation-form-section">
            <h3>Criterios de Evaluación</h3>

            {error && <ErrorMessage message={error} type="error" onClose={() => setError(null)} />}

            {criteria.length === 0 ? (
              <div className="no-criteria">
                <p>⚠️ No hay criterios de evaluación configurados para este tipo de video</p>
              </div>
            ) : (
              <div className="criteria-list">
                {criteria.map((criterion, index) => (
                  <div key={criterion.criterion_id} className="criterion-item">
                    <div className="criterion-header">
                      <h4>
                        {index + 1}️⃣ {criterion.name} 
                        ({criterion.min_score}-{criterion.max_score} pts)
                      </h4>
                      {criterion.description && (
                        <p className="criterion-description">{criterion.description}</p>
                      )}
                    </div>

                    <div className="criterion-input">
                      <label>Score:</label>
                      <input
                        type="number"
                        min={criterion.min_score}
                        max={criterion.max_score}
                        value={evaluations[criterion.criterion_id] || ''}
                        onChange={(e) => handleScoreChange(
                          criterion.criterion_id, 
                          Number(e.target.value)
                        )}
                        className="score-input"
                      />
                    </div>

                    <div className="criterion-comment">
                      <label>Comentarios:</label>
                      <textarea
                        value={comments[criterion.criterion_id] || ''}
                        onChange={(e) => handleCommentChange(
                          criterion.criterion_id,
                          e.target.value
                        )}
                        placeholder="Comentarios opcionales..."
                        rows={3}
                        className="comment-textarea"
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="evaluation-total">
              <strong>Total: {calculateTotalScore()} puntos</strong>
            </div>

            <div className="evaluation-actions">
              {onNavigate && (
                <Button
                  variant="outline"
                  onClick={() => onNavigate('prev')}
                  disabled={isSaving || criteria.length === 0}
                >
                  ◀ Anterior
                </Button>
              )}

              <Button
                variant="secondary"
                onClick={() => handleSave(false)}
                loading={isSaving}
                disabled={isSaving || criteria.length === 0}
              >
                Guardar Borrador
              </Button>

              <Button
                variant="primary"
                onClick={() => handleSave(true)}
                loading={isSaving}
                disabled={isSaving || criteria.length === 0}
              >
                Finalizar ✅
              </Button>

              {onNavigate && (
                <Button
                  variant="outline"
                  onClick={() => onNavigate('next')}
                  disabled={isSaving || criteria.length === 0}
                >
                  Siguiente ▶
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default JudgeVideoEvaluationModal;